import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import type { TaxonomyTreeNode } from '@/app/audience-builder/services/fetch-intent-topics';
import type { TechnographicSelection, TechnographicTier } from './types';
import { fetchTechnographicTree, type TechnographicTree } from './fetch-technographics';
import type { SavedAudienceTechnology } from '@/app/intelligence-audience/lib/audience-definition';

/**
 * A saved audience's technology, to and from the selections the drawer edits (NPJB-764).
 *
 * The definition stores bare ids: `uses` is "Uses", `notUses` "Doesn't use" (NPJB-787). A
 * selection also needs a label and a tier, and only the technology tree knows them — it holds
 * every node, products included, so an id saved months ago still finds its name.
 */

/** What the tree says about one id. */
export interface TechnographicNode {
  label: string;
  tier: TechnographicSelection['tier'];
}

/** Products are `ENTITY` rows; every node above them is a level the server expands. */
const PRODUCT_TYPE = 'ENTITY';

/**
 * The tier a node holds, from its own place in the tree (NPJB-661: Category → Group → Type →
 * Products). A top node is a Category; a node with products right under it is a Type; any
 * other level between is a Group. Decided per node, not per tree depth: a branch with no Group
 * level still reads Category → Type, beside a branch that has one.
 */
export const tierOf = (node: TaxonomyTreeNode, depth: number): TechnographicTier => {
  if (node.type === PRODUCT_TYPE) return 'product';
  if (depth === 0) return 'category';
  return node.children.some((c) => c.type === PRODUCT_TYPE) ? 'type' : 'group';
};

/** What a chip names its tier with. A product shows no tag. */
export const TIER_LABEL: Record<TechnographicTier, string> = {
  category: 'Category',
  group: 'Group',
  type: 'Type',
  product: '',
};

/** How many levels sit above products on the deepest branch: 2 for Category → Type. */
const levelsAboveProducts = (nodes: TaxonomyTreeNode[]): number => {
  let deepest = 0;
  for (const node of nodes) {
    if (node.type === PRODUCT_TYPE) continue;
    deepest = Math.max(deepest, 1 + levelsAboveProducts(node.children));
  }
  return deepest;
};

/**
 * Column headers for the deepest branch, top down: Category / Type / Products, or Category /
 * Group / Type / Products. A column shown for a shorter branch names itself from its own nodes
 * (`technographicColumnName`); these are the fallback for a column with nothing in it yet.
 */
export const technographicHeaders = (nodes: TaxonomyTreeNode[]): string[] => {
  const levels = Math.max(levelsAboveProducts(nodes), 1);
  const middle = Array.from({ length: levels - 1 }, (_, i) =>
    i === levels - 2 ? TIER_LABEL.type : TIER_LABEL.group,
  );
  return [TIER_LABEL.category, ...middle, 'Products'];
};

/** What an empty column holds, named by the column to its left. */
const NEXT_LEVEL: Record<string, string> = {
  [TIER_LABEL.group]: TIER_LABEL.type,
  [TIER_LABEL.type]: 'Products',
  [`${TIER_LABEL.group} / ${TIER_LABEL.type}`]: 'Products',
  Products: '',
};

/**
 * A browse column's header, from the nodes it is showing. An empty column is named by the one
 * to its left (after Type come Products; past Products, nothing) — or `undefined` while that is
 * still the Category, so the caller falls back to `technographicHeaders`. Siblings of mixed
 * kinds read "Group / Type".
 */
export const technographicColumnName = (
  nodes: readonly TaxonomyTreeNode[] | null,
  level: number,
  isProducts: boolean,
  previous?: string,
): string | undefined => {
  if (isProducts) return 'Products';
  if (nodes === null || nodes.length === 0) {
    return previous === undefined ? undefined : NEXT_LEVEL[previous];
  }
  const kinds = [...new Set(nodes.map((n) => TIER_LABEL[tierOf(n, level)]))];
  return kinds.length === 1 ? kinds[0] : [TIER_LABEL.group, TIER_LABEL.type].join(' / ');
};

interface TreeIndex {
  nodes: Map<string, TechnographicNode>;
  /** The tree's own node per id, so a count can walk under it. */
  raw: Map<string, TaxonomyTreeNode>;
  /** Product counts worked out so far, by id. */
  counts: Map<string, number>;
}

/** One index per tree: the chips, the modal and search all share it instead of each walking
 * a ~219k-node tree again. Keyed on the tree array, so a refetched tree builds a fresh one. */
const INDEXES = new WeakMap<TaxonomyTreeNode[], TreeIndex>();

const indexLevel = (nodes: TaxonomyTreeNode[], depth: number, index: TreeIndex): void => {
  for (const node of nodes) {
    index.nodes.set(node.topicId, { label: node.label, tier: tierOf(node, depth) });
    if (!index.raw.has(node.topicId)) index.raw.set(node.topicId, node);
    indexLevel(node.children, depth + 1, index);
  }
};

const treeIndex = (tree: TaxonomyTreeNode[]): TreeIndex => {
  const cached = INDEXES.get(tree);
  if (cached) return cached;
  const index: TreeIndex = { nodes: new Map(), raw: new Map(), counts: new Map() };
  indexLevel(tree, 0, index);
  INDEXES.set(tree, index);
  return index;
};

/** Every node in the technology tree, by id, with the tier its place gives it. */
export const indexTechnographicTree = (
  nodes: TaxonomyTreeNode[],
): ReadonlyMap<string, TechnographicNode> => treeIndex(nodes).nodes;

const collectProducts = (node: TaxonomyTreeNode, out: Set<string>): void => {
  if (node.type === PRODUCT_TYPE) {
    out.add(node.topicId);
    return;
  }
  for (const child of node.children) collectProducts(child, out);
};

/**
 * How many distinct products a Category, Group or Type covers (NPJB-787 AC5): "CRM · 1,549
 * products". Ids, not a tally — the tree keeps poly-hierarchy, so a product filed under two
 * parents counts once. Worked out only for ids someone picked, then kept. `undefined` for a
 * product or an id the tree does not hold.
 */
export const technographicProductCount = (
  tree: TaxonomyTreeNode[],
  topicId: string,
): number | undefined => {
  const index = treeIndex(tree);
  const node = index.raw.get(topicId);
  if (node === undefined || node.type === PRODUCT_TYPE) return undefined;
  const known = index.counts.get(topicId);
  if (known !== undefined) return known;
  const products = new Set<string>();
  collectProducts(node, products);
  index.counts.set(topicId, products.size);
  return products.size;
};

/** The selections as the definition stores them. */
export const technologyFromSelections = (
  selections: readonly TechnographicSelection[],
): SavedAudienceTechnology => ({
  uses: selections.filter((s) => s.mode === 'runs').map((s) => s.topicId),
  notUses: selections.filter((s) => s.mode === 'notRuns').map((s) => s.topicId),
});

/**
 * The definition's technology as selections. An id the tree cannot name keeps the id as its
 * label rather than disappearing, and is read as a product — it matches only itself.
 */
export const selectionsFromTechnology = (
  technology: SavedAudienceTechnology,
  nodeFor: (topicId: string) => TechnographicNode | undefined,
): TechnographicSelection[] => {
  const toSelection =
    (mode: TechnographicSelection['mode']) =>
    (topicId: string): TechnographicSelection => {
      const node = nodeFor(topicId);
      return { topicId, label: node?.label ?? topicId, tier: node?.tier ?? 'product', mode };
    };
  return [
    ...technology.uses.map(toSelection('runs')),
    ...technology.notUses.map(toSelection('notRuns')),
  ];
};

/**
 * Selections with the tree's names and tiers put back on (NPJB-943). The Edit drawer restores an
 * audience's technology before the tree has loaded, so its picks start as products named by their
 * id; this names them once the tree is read. Ids and conditions never change. Returns the same
 * array when nothing changes, so a store can skip the update.
 */
export const nameSelections = (
  selections: TechnographicSelection[],
  nodeFor: (topicId: string) => TechnographicNode | undefined,
): TechnographicSelection[] => {
  let changed = false;
  const named = selections.map((selection) => {
    const node = nodeFor(selection.topicId);
    if (node === undefined) return selection;
    if (node.label === selection.label && node.tier === selection.tier) return selection;
    changed = true;
    return { ...selection, label: node.label, tier: node.tier };
  });
  return changed ? named : selections;
};

/** The tree query, shared by the browse modal's hook and the restore below. */
export const technographicTreeQuery = {
  queryKey: queryKeys.technographics.tree(),
  queryFn: fetchTechnographicTree,
  staleTime: 30 * 60 * 1000,
};

/** The tree, if it is already in the cache — read without asking the server. */
export const cachedTechnographicIndex = (
  queryClient: QueryClient,
): ReadonlyMap<string, TechnographicNode> | undefined => {
  const cached = queryClient.getQueryData<TechnographicTree>(technographicTreeQuery.queryKey);
  return cached === undefined ? undefined : indexTechnographicTree(cached.tree);
};

/**
 * Restore a saved audience's technology. The tree is fetched only when there is technology to
 * name, and its cached copy is reused. The tier must be right, not just the label: the count
 * stream expands a category and matches a product alone. If the tree cannot be read, the ids
 * still restore — as products, which is what an unknown id is.
 */
export const resolveTechnology = async (
  technology: SavedAudienceTechnology,
  queryClient: QueryClient,
): Promise<TechnographicSelection[]> => {
  if (technology.uses.length === 0 && technology.notUses.length === 0) return [];
  try {
    const { tree } = await queryClient.fetchQuery(technographicTreeQuery);
    const index = indexTechnographicTree(tree);
    return selectionsFromTechnology(technology, (id) => index.get(id));
  } catch {
    return selectionsFromTechnology(technology, () => undefined);
  }
};
